import { createClient } from '@/utils/supabase/client'

// Cache configuration
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes
const BACKGROUND_REFRESH_TTL = 2 * 60 * 1000 // 2 minutes

interface CacheEntry<T> {
  data: T
  timestamp: number
  promise?: Promise<T>
}

interface CacheConfig {
  ttl?: number
  backgroundRefresh?: boolean
  backgroundRefreshTtl?: number
}

class SupabaseCache {
  private cache = new Map<string, CacheEntry<any>>()
  private subscribers = new Map<string, Set<(data: any) => void>>()

  // Generate cache key based on query and user context
  private getCacheKey(query: string, userId?: string): string {
    return userId ? `${userId}:${query}` : query
  }

  // Subscribe to cache updates
  subscribe<T>(key: string, callback: (data: T) => void): () => void {
    if (!this.subscribers.has(key)) {
      this.subscribers.set(key, new Set())
    }
    this.subscribers.get(key)!.add(callback)
    
    return () => {
      const subs = this.subscribers.get(key)
      if (subs) {
        subs.delete(callback)
        if (subs.size === 0) {
          this.subscribers.delete(key)
        }
      }
    }
  }

  // Notify subscribers of cache updates
  private notifySubscribers<T>(key: string, data: T) {
    const subs = this.subscribers.get(key)
    if (subs) {
      subs.forEach(callback => callback(data))
    }
  }

  // Get cached data or fetch if needed
  async get<T>(
    key: string,
    fetcher: () => Promise<T>,
    config: CacheConfig = {}
  ): Promise<T> {
    const { ttl = CACHE_TTL, backgroundRefresh = true, backgroundRefreshTtl = BACKGROUND_REFRESH_TTL } = config
    const now = Date.now()
    const entry = this.cache.get(key)

    // If we have valid cached data, return it
    if (entry && (now - entry.timestamp) < ttl) {
      // Trigger background refresh if enabled and enough time has passed
      if (backgroundRefresh && (now - entry.timestamp) > backgroundRefreshTtl) {
        this.backgroundRefresh(key, fetcher)
      }
      // Ensure arrays are never null
      const safeData = Array.isArray(entry.data) ? (entry.data || []) : entry.data
      return safeData
    }

    // If there's an ongoing request, wait for it
    if (entry?.promise) {
      return entry.promise
    }

    // Fetch new data
    const promise = fetcher().then(data => {
      // Ensure arrays are never null
      const safeData = Array.isArray(data) ? (data || []) : data
      this.cache.set(key, { data: safeData, timestamp: now })
      this.notifySubscribers(key, safeData)
      return safeData
    })

    // Store the promise to prevent duplicate requests
    this.cache.set(key, { data: null, timestamp: now, promise })
    
    return promise
  }

  // Background refresh without blocking
  private async backgroundRefresh<T>(key: string, fetcher: () => Promise<T>) {
    try {
      const data = await fetcher()
      const now = Date.now()
      // Ensure arrays are never null
      const safeData = Array.isArray(data) ? (data || []) : data
      this.cache.set(key, { data: safeData, timestamp: now })
      this.notifySubscribers(key, safeData)
    } catch (error) {
      console.warn('Background refresh failed for key:', key, error)
    }
  }

  // Invalidate cache entries
  invalidate(pattern?: string) {
    if (pattern) {
      // Invalidate entries matching pattern
      for (const key of this.cache.keys()) {
        if (key.includes(pattern)) {
          this.cache.delete(key)
        }
      }
    } else {
      // Clear all cache
      this.cache.clear()
    }
  }

  // Update cache entry
  set<T>(key: string, data: T) {
    this.cache.set(key, { data, timestamp: Date.now() })
    this.notifySubscribers(key, data)
  }

  // Get cache stats
  getStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    }
  }
}

// Global cache instance
export const supabaseCache = new SupabaseCache()

// Predefined cache keys
export const CACHE_KEYS = {
  USER: (userId: string) => `user:${userId}`,
  WORKSPACES: (userId: string) => `workspaces:${userId}`,
  CURRENT_WORKSPACE: (userId: string) => `current-org:${userId}`,
  SHOWS: 'shows:all',
  RUNS: (userId: string, orgId?: string) => `runs:${userId}:${orgId || 'personal'}`,
  CART: (userId: string, orgId?: string) => `cart:${userId}:${orgId || 'personal'}`,
  PUBLIC_PROFILE: (userId: string) => `profile:${userId}`,
  INVITATIONS: (email: string) => `invitations:${email}`,
  CAMPAIGNS: (workspaceId: string) => `campaigns:${workspaceId}`,
  CAMPAIGN: (campaignId: string) => `campaign:${campaignId}`,
  SETTINGS: (workspaceId: string) => `settings:${workspaceId}`,
  TEAM: (workspaceId: string) => `team:${workspaceId}`,
} as const

// Utility function to get current user ID
export const getCurrentUserId = async (): Promise<string | null> => {
  const supabase = createClient()
  const { data: { user } } = await supabase.auth.getUser()
  return user?.id || null
}

// Utility function to get current workspace ID
export const getCurrentWorkspaceId = (): string | null => {
  if (typeof window === 'undefined') return null
  const stored = localStorage.getItem('currentWorkspaceId')
  return stored === 'personal' ? null : stored
}
